/* ==========================================================================
   MDS · Subscription → straight to Shopify checkout
   --------------------------------------------------------------------------
   THE PROBLEM THIS SOLVES
     On a product page, choosing "Subscribe" and hitting Add to cart puts the
     item in the cart. From the cart, the shopper goes to the custom checkout
     (checkout.mydormstore.ca), the selling_plan gets dropped along the way,
     and no subscription contract is ever created in Shopify.

   WHAT THIS DOES
     It watches every product form on the page. When someone adds to cart AND
     a selling plan is selected (i.e. they picked a subscription, not one-time),
     it cancels the normal add, adds the item itself with the selling plan
     attached, and sends them straight to /checkout — Shopify's own checkout,
     which is the only one that can create a subscription contract.

     One-time purchases are untouched. They add to cart exactly as they do now
     and still flow through your custom checkout.

   ──────────────────────────────────────────────────────────────────────────
   HOW TO INSTALL
     1. Online Store > Themes > Edit code > Assets > Add a new asset
        Name it:  mds-subscription-direct-checkout.js
        Paste this file in.
     2. Open  layout/theme.liquid  and add this line just before </body>:

          {{ 'mds-subscription-direct-checkout.js' | asset_url | script_tag }}

        (Or put it in your product template if you'd rather it only load there.)

   ──────────────────────────────────────────────────────────────────────────
   PLEASE TEST BEFORE TRUSTING IT
     I can't see your live theme, so this detects the selling plan by looking
     for the standard Shopify input names. Themes and subscription apps vary.
     Do one real $30 subscription order, then check:

       Shopify Admin > Customers > that customer > Subscriptions

     A subscription contract should be listed there. If it isn't, open the
     browser console on the product page — this script logs what it found
     under the "[MDS sub]" prefix, and that tells us what to adjust.

     Also confirm that /checkout lands on Shopify's checkout and your custom
     checkout app isn't also intercepting it.
   ========================================================================== */

(function () {
  "use strict";

  var LOG = "[MDS sub]";

  /* Field names Shopify and the common subscription apps use for the plan.
     If your theme uses something else, add it here. */
  var PLAN_FIELDS = [
    "selling_plan",
    "properties[selling_plan]",
    "selling_plan_id",
  ];

  /* ---------------------------------------------------------------------- */

  function findSellingPlan(form) {
    for (var i = 0; i < PLAN_FIELDS.length; i++) {
      var field = form.querySelector('[name="' + PLAN_FIELDS[i] + '"]');
      if (!field) continue;

      // A radio group: only the checked one counts.
      if (field.type === "radio") {
        var checked = form.querySelector(
          '[name="' + PLAN_FIELDS[i] + '"]:checked'
        );
        if (checked && checked.value) return checked.value;
        continue;
      }

      if (field.value && field.value !== "0" && field.value !== "") {
        return field.value;
      }
    }
    return null;
  }

  function findVariantId(form) {
    var el =
      form.querySelector('[name="id"]:checked') ||
      form.querySelector('[name="id"]');
    return el && el.value ? el.value : null;
  }

  function collectLineItemProperties(form) {
    var props = {};
    form.querySelectorAll("[name^='properties[']").forEach(function (el) {
      if (el.type === "checkbox" && !el.checked) return;
      if (el.type === "radio" && !el.checked) return;
      var match = el.name.match(/^properties\[(.+)\]$/);
      if (!match) return;
      if (match[1] === "selling_plan") return; // handled separately
      if (el.value) props[match[1]] = el.value;
    });
    return props;
  }

  function setBusy(form, busy, originalLabel) {
    var btn = form.querySelector(
      '[type="submit"], [name="add"], .product-form__submit'
    );
    if (!btn) return null;
    if (busy) {
      var label = btn.textContent;
      btn.disabled = true;
      btn.setAttribute("aria-busy", "true");
      btn.textContent = "Taking you to checkout…";
      return label;
    }
    btn.disabled = false;
    btn.removeAttribute("aria-busy");
    if (originalLabel) btn.textContent = originalLabel;
    return null;
  }

  /* ---------------------------------------------------------------------- */

  document.addEventListener(
    "submit",
    function (ev) {
      var form = ev.target;
      if (!(form instanceof HTMLFormElement)) return;

      // Only Shopify add-to-cart forms.
      var action = form.getAttribute("action") || "";
      if (action.indexOf("/cart/add") === -1) return;

      var plan = findSellingPlan(form);
      if (!plan) return; // one-time purchase — leave it completely alone

      var variantId = findVariantId(form);
      if (!variantId) {
        console.warn(LOG, "selling plan found but no variant id — standing down");
        return;
      }

      ev.preventDefault();
      ev.stopPropagation();

      var qtyEl = form.querySelector('[name="quantity"]');
      var quantity = qtyEl && qtyEl.value ? parseInt(qtyEl.value, 10) : 1;
      if (!quantity || quantity < 1) quantity = 1;

      var payload = {
        items: [
          {
            id: parseInt(variantId, 10),
            quantity: quantity,
            selling_plan: parseInt(plan, 10),
            properties: Object.assign(collectLineItemProperties(form), {
              _subscription_source: "product-page-direct-checkout",
            }),
          },
        ],
      };

      console.log(LOG, "subscription detected, going direct to checkout", payload);

      var originalLabel = setBusy(form, true);

      fetch("/cart/add.js", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Accept: "application/json",
        },
        body: JSON.stringify(payload),
      })
        .then(function (resp) {
          if (resp.ok) return resp.json();
          return resp.json().then(
            function (err) {
              throw new Error(err.description || "Add to cart failed");
            },
            function () {
              throw new Error("Add to cart failed (" + resp.status + ")");
            }
          );
        })
        .then(function () {
          // Shopify's native checkout. NOT /cart — the cart hands the shopper
          // to the custom checkout, which is where the selling plan is lost.
          window.location.href = "/checkout";
        })
        .catch(function (e) {
          console.error(LOG, "failed:", e);
          setBusy(form, false, originalLabel);
          // Last resort: Shopify's server-side permalink does the same job.
          window.location.href =
            "/cart/" +
            variantId +
            ":" +
            quantity +
            "?selling_plan=" +
            plan +
            "&return_to=/checkout";
        });
    },
    true // capture phase, so we get there before the theme's own handler
  );

  console.log(LOG, "direct-to-checkout handler active");
})();